class ExitPopup {
  constructor() {
    this.popup = document.getElementById('exit-popup');
    if (!this.popup) return;

    this.isOpen = false;
    this.hasShown = false;
    this.allowLeave = false;
    this.isActive = true;
    this.timerId = null;
    this.idleDelayMs = this.getIdleDelayMs();

    this.boundPopState = this.handlePopState.bind(this);
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundClose = this.handleClose.bind(this);
    this.boundCta = this.handleCta.bind(this);
    this.boundLinkClick = this.handleLinkClick.bind(this);
    this.boundActivity = this.handleActivity.bind(this);
    this.needsRearm = false;

    this.init();
  }

  init() {
    this.closeTargets = this.popup.querySelectorAll('[data-exit-close]');
    this.ctaLink = this.popup.querySelector('[data-exit-cta]');

    this.closeTargets.forEach((el) => el.addEventListener('click', this.boundClose));
    if (this.ctaLink) {
      this.ctaLink.addEventListener('click', this.boundCta);
    }

    document.addEventListener('keydown', this.boundKeydown);
    document.addEventListener('click', this.boundLinkClick);
    window.addEventListener('popstate', this.boundPopState);
    window.addEventListener('beforeunload', this.boundBeforeUnload);

    if (this.idleDelayMs !== null) {
      this.startIdleTimer();
    }

    this.ensureHistoryTrap();
  }

  getIdleDelayMs() {
    const rawDelay = this.popup.dataset.exitIdle;
    if (!rawDelay) return null;

    const parsed = Number.parseInt(rawDelay, 10);
    if (Number.isNaN(parsed) || parsed < 0) return null;

    return parsed;
  }

  startIdleTimer() {
    this.clearIdleTimer();
    this.timerId = window.setTimeout(() => this.handleIdleTimeout(), this.idleDelayMs);
    this.bindActivityListeners();
  }

  clearIdleTimer() {
    if (this.timerId) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  bindActivityListeners() {
    document.addEventListener('scroll', this.boundActivity, { passive: true });
    document.addEventListener('touchstart', this.boundActivity, { passive: true });
    document.addEventListener('keydown', this.boundActivity);
    document.addEventListener('click', this.boundActivity);
  }

  unbindActivityListeners() {
    document.removeEventListener('scroll', this.boundActivity);
    document.removeEventListener('touchstart', this.boundActivity);
    document.removeEventListener('keydown', this.boundActivity);
    document.removeEventListener('click', this.boundActivity);
  }

  handleActivity() {
    if (this.hasShown) return;
    if (this.idleDelayMs === null) return;
    this.startIdleTimer();
  }

  handleIdleTimeout() {
    if (this.allowLeave || this.hasShown) return;
    this.show();
  }

  ensureHistoryTrap() {
    if (!window.history || !window.history.pushState) return;

    window.history.pushState({ exitPopup: true }, '', window.location.href);
  }

  handlePopState() {
    if (this.allowLeave) return;

    window.history.pushState({ exitPopup: true }, '', window.location.href);
    this.show();
  }

  handleBeforeUnload(event) {
    if (this.allowLeave) return;

    // Most browsers only allow the default confirmation dialog here.
    event.preventDefault();
    event.returnValue = '';
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      this.close();
    }
  }

  handleClose(event) {
    if (event) event.preventDefault();
    this.close();
  }

  handleCta() {
    this.allowLeave = true;
    this.needsRearm = false;
  }

  handleLinkClick(event) {
    const link = event.target.closest('a');
    if (!link) return;

    if (link.hasAttribute('data-exit-cta')) return;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return;

    const target = link.getAttribute('target');
    if (target && target !== '_self') return;

    if (href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

    this.allowLeave = true;
  }

  show() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.hasShown = true;

    // Save scroll position before locking body
    this.scrollY = window.scrollY;
    document.body.style.top = `-${this.scrollY}px`;
    document.body.classList.add('exit-popup-open');

    this.popup.classList.add('is-open');
    this.popup.setAttribute('aria-hidden', 'false');

    this.clearIdleTimer();
    this.unbindActivityListeners();
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.popup.classList.remove('is-open');
    this.popup.setAttribute('aria-hidden', 'true');

    // Restore scroll position
    document.body.classList.remove('exit-popup-open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollY);

    if (!this.allowLeave && this.needsRearm) {
      this.needsRearm = false;
      this.ensureHistoryTrap();
    }
  }

  destroy() {
    if (!this.isActive) return;

    this.closeTargets.forEach((el) => el.removeEventListener('click', this.boundClose));
    if (this.ctaLink) {
      this.ctaLink.removeEventListener('click', this.boundCta);
    }

    document.removeEventListener('keydown', this.boundKeydown);
    document.removeEventListener('click', this.boundLinkClick);
    window.removeEventListener('popstate', this.boundPopState);
    window.removeEventListener('beforeunload', this.boundBeforeUnload);

    this.clearIdleTimer();
    this.unbindActivityListeners();

    this.isActive = false;
  }
}

const initExitPopup = () => {
  if (window.exitPopupInstance && window.exitPopupInstance.destroy) {
    window.exitPopupInstance.destroy();
  }

  const instance = new ExitPopup();
  if (instance && instance.isActive) {
    window.exitPopupInstance = instance;
  } else {
    window.exitPopupInstance = null;
  }
};

document.addEventListener('DOMContentLoaded', initExitPopup);
document.addEventListener('turbolinks:load', initExitPopup);

export default ExitPopup;
